// src/hooks/useAuthRole.js
// Watches Firebase Auth; reads Firestore users/{uid}.role; exposes helpers.
// Default (lowest) role is "new_register".

import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../Firebase/firebase.init'; // keep your exact path/casing

/* =========================================================
   Role ladder (LOWEST → HIGHEST)
   IMPORTANT: Keep this order in sync with your backend RBAC.
   ========================================================= */
const ROLE_ORDER = [
  // Base
  'new_register',
  'user',

  // Onboarding / new roles
  'new_sales',
  'new_production',
  'new_finance',
  'new_hr',
  'new_administrative',

  // Regular / active department roles
  'sales',
  'production',
  'finance',
  'hr',
  'administrative',

  // Elevated roles
  'operator',
  'moderator',
  'admin',
];

/** Normalize any incoming role string to a known role; fallback to "new_register". */
function normalizeRole(value) {
  const v = String(value || '').trim().toLowerCase();
  return ROLE_ORDER.includes(v) ? v : 'new_register';
}

export function useAuthRole() {
  /* ------------------------------ state ------------------------------ */
  const [user, setUser] = useState(null); // Firebase Auth user object
  const [role, setRole] = useState('new_register'); // default baseline role
  const [loading, setLoading] = useState(true); // loading user/role
  const [error, setError] = useState(''); // capture Firestore errors safely

  /* -------------------------------------------------------------------
     gte(a, b) => whether role "a" is >= role "b" in ROLE_ORDER ranking.
     Example: gte("admin","operator") === true
              gte("user","sales") === false
     ------------------------------------------------------------------- */
  const gte = useCallback(
    (a, b) =>
      ROLE_ORDER.indexOf(normalizeRole(a)) >=
      ROLE_ORDER.indexOf(normalizeRole(b)),
    []
  );

  /* ------------------------- exact-role flags ------------------------- */
  const isNewRegister = useMemo(() => role === 'new_register', [role]);
  const isUser = useMemo(() => role === 'user', [role]);

  // Onboarding / new roles
  const isNewSales = useMemo(() => role === 'new_sales', [role]);
  const isNewProduction = useMemo(() => role === 'new_production', [role]);
  const isNewFinance = useMemo(() => role === 'new_finance', [role]);
  const isNewHr = useMemo(() => role === 'new_hr', [role]);
  const isNewAdministrative = useMemo(
    () => role === 'new_administrative',
    [role]
  );

  // Active department roles
  const isSales = useMemo(() => role === 'sales', [role]);
  const isProduction = useMemo(() => role === 'production', [role]);
  const isFinance = useMemo(() => role === 'finance', [role]);
  const isHr = useMemo(() => role === 'hr', [role]);
  const isAdministrative = useMemo(() => role === 'administrative', [role]);

  // Elevated roles
  const isOperator = useMemo(() => role === 'operator', [role]);
  const isModerator = useMemo(() => role === 'moderator', [role]);
  const isAdmin = useMemo(() => role === 'admin', [role]);

  /* ------------------------ inclusive access helpers ------------------------
     NOTE:
     - Use gte(...) ONLY when you truly want every role above it to pass.
     - For department routers, sometimes you must restrict and NOT include
       neighboring departments.
     ------------------------------------------------------------------------ */

  // Sales and everything above it in ROLE_ORDER
  // (includes production/finance/hr/administrative/operator/moderator/admin)
  const isSalesPlus = useMemo(() => gte(role, 'sales'), [gte, role]);

  // ✅ New Sales access: ONLY new_sales OR operator/moderator/admin
  const isNewSalesPlus = useMemo(
    () => role === 'new_sales' || role === 'sales' || gte(role, 'operator'),
    [gte, role]
  ); // new_sales | operator | moderator | admin

  // ✅ New Production access: ONLY new_production OR operator/moderator/admin
  // (NOT production, not other departments)
  const isNewProductionPlus = useMemo(
    () => role === 'new_production' || role === 'production' || gte(role, 'operator'),
    [gte, role]
  ); // new_production | operator | moderator | admin

  // ✅ Production access: ONLY production OR operator/moderator/admin
  // Do NOT use gte(role,'production') because that would include finance/hr/administrative too.
  const isProductionPlus = useMemo(
    () => role === 'production' || gte(role, 'operator'),
    [gte, role]
  ); // production | operator | moderator | admin

  // ✅ Finance access: ONLY finance OR moderator/admin
  // Do NOT use gte(role,'finance') because that would include hr/administrative/operator too.
  const isFinancePlus = useMemo(
    () => role === 'finance' || gte(role, 'moderator'),
    [gte, role]
  ); // finance | moderator | admin

  // ✅ HR access: ONLY hr OR moderator/admin
  // Do NOT use gte(role,'hr') because that would include administrative/operator too.
  const isHrRouterPlus = useMemo(
    () => role === 'hr' || gte(role, 'moderator'),
    [gte, role]
  ); // hr | moderator | admin

  // ✅ New HR access: ONLY new_hr OR moderator/admin
  const isNewHrPlus = useMemo(
    () => role === 'new_hr' || role === 'hr' || gte(role, 'moderator'),
    [gte, role]
  ); // new_hr | moderator | admin

  // ✅ New Finance access: ONLY new_finance OR moderator/admin
  const isNewFinancePlus = useMemo(
    () => role === 'new_finance' || role === 'finance' || gte(role, 'moderator'),
    [gte, role]
  ); // new_finance | moderator | admin

  // ✅ New Administrative access: ONLY new_administrative OR operator/moderator/admin
  const isNewAdministrativePlus = useMemo(
    () => role === 'new_administrative' || role === 'administrative' || gte(role, 'operator'),
    [gte, role]
  ); // new_administrative | operator | moderator | admin

  // Administrative and above (operator/moderator/admin)
  const isAdministrativePlus = useMemo(() => gte(role, 'administrative'), [
    gte,
    role,
  ]); // administrative | operator | moderator | admin

  // Elevated inclusives
  const isOperatorPlus = useMemo(() => gte(role, 'operator'), [gte, role]); // operator | moderator | admin
  const isModeratorPlus = useMemo(() => gte(role, 'moderator'), [gte, role]); // moderator | admin
  const isAdminPlus = useMemo(() => gte(role, 'admin'), [gte, role]); // admin

  /* ------------------------ Firestore role fetching ------------------------ */
  // Read role from Firestore: users/{uid}.role
  const fetchRoleForUid = useCallback(async (uid) => {
    try {
      setError('');

      // No uid = treat as signed out / baseline
      if (!uid) {
        setRole('new_register');
        return;
      }

      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);

      // Read role field; if missing/unknown -> normalizeRole handles fallback
      const raw = snap.exists() ? snap.data()?.role : undefined;
      setRole(normalizeRole(raw));
    } catch (e) {
      // Safe fallback; backend must still enforce RBAC
      setRole('new_register');
      setError(e?.message || 'Failed to read user role');
    }
  }, []);

  /* ---------------------- sync with Firebase auth state --------------------- */
  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!mounted) return;

      // Signed out
      if (!u) {
        setUser(null);
        setRole('new_register');
        setLoading(false);
        return;
      }

      // Signed in
      setUser(u);
      setLoading(true);
      await fetchRoleForUid(u.uid);
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [fetchRoleForUid]);

  /* -------------------------- manual refresh helper ------------------------- */
  // Use after an admin changes role; re-fetch users/{uid}.role.
  const refreshRole = useCallback(async () => {
    setLoading(true);
    await fetchRoleForUid(user ? user.uid : '');
    setLoading(false);
  }, [fetchRoleForUid, user]);

  /* ------------------------------ public API ------------------------------ */
  return {
    // state
    user, // Firebase Auth user or null
    role, // normalized role string
    loading,
    isLoading: loading, // alias
    error,

    // exact-role flags
    isNewRegister,
    isUser,

    isNewSales,
    isNewProduction,
    isNewFinance,
    isNewHr,
    isNewAdministrative,

    isSales,
    isProduction,
    isFinance,
    isHr,
    isAdministrative,

    isOperator,
    isModerator,
    isAdmin,

    // inclusive helpers (for routers/menus)
    isSalesPlus,
    isNewSalesPlus,
    isNewProductionPlus,
    isProductionPlus,
    isFinancePlus,
    isHrRouterPlus,
    isNewHrPlus,
    isNewFinancePlus,
    isNewAdministrativePlus,
    isAdministrativePlus,
    isOperatorPlus,
    isModeratorPlus,
    isAdminPlus,

    // utils
    gte,
    refreshRole,
  };
}

export default useAuthRole;
